import { useEffect, useLayoutEffect, useState } from "react";

import {
  selectInitialWindowSize,
  setInitialWindowSize,
  type WindowDimensions,
} from "@/features/app/appSlice";

import store from "../store";
import { useAppSelector } from ".";

export const getWindowSize = () => {
  // visualViewport reflects the actual visible area on iOS Safari when the
  // software keyboard is open. window.innerHeight does not shrink on iOS,
  // which causes the bottom sheet to overflow above the screen when typing.
  const height =
    window.visualViewport?.height ?? window.innerHeight ?? window.outerHeight;
  const width =
    window.visualViewport?.width ?? window.innerWidth ?? window.outerWidth;
  return { height, width };
};

export const updateInitialWindowSize = (
  size: { width: number; height: number },
  initialSize: { width: number; height: number },
) => {
  if (
    size.height == 0 ||
    size.width == 0 ||
    initialSize.width != 0 ||
    initialSize.height != 0
  )
    return;

  store.dispatch(
    setInitialWindowSize({
      height: size.height,
      width: size.width,
    }),
  );
};

const useWindowDimensions = () => {
  const initialWindowSize = useAppSelector(selectInitialWindowSize);
  const [windowSize, setWindowSize] = useState<WindowDimensions>(
    getWindowSize(),
  );
  // Set up initial window size
  useLayoutEffect(() => {
    const dimensions = getWindowSize();

    updateInitialWindowSize(dimensions, initialWindowSize);
  }, []);

  useEffect(() => {
    updateInitialWindowSize(windowSize, initialWindowSize);
  }, [windowSize]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      setWindowSize(getWindowSize());
    });
    resizeObserver.observe(document.body);

    // visualViewport fires on iOS Safari when the software keyboard appears or
    // disappears, since window.innerHeight does not change in that case.
    const onVisualViewportResize = () => {
      setWindowSize(getWindowSize());
    };
    window.visualViewport?.addEventListener("resize", onVisualViewportResize);

    updateInitialWindowSize(windowSize, initialWindowSize);

    return () => {
      resizeObserver.disconnect();
      window.visualViewport?.removeEventListener(
        "resize",
        onVisualViewportResize,
      );
    };
  }, []);

  return windowSize;
};

export default useWindowDimensions;
